import React, { useState } from "react";
import { toast } from "sonner";import { Transaction, Merchant, Provider } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { ChevronLeft, ChevronRight, ArrowUpFromLine, Plus, History } from "lucide-react";

const THAI_BANKS = [
  { code: "SCB", name: "Siam Commercial Bank" },
  { code: "KBANK", name: "Kasikorn Bank" },
  { code: "BBL", name: "Bangkok Bank" },
  { code: "KTB", name: "Krung Thai Bank" },
  { code: "BAY", name: "Bank of Ayudhya (Krungsri)" },
  { code: "TTB", name: "TMBThanachart Bank" },
  { code: "GSB", name: "Government Savings Bank" },
];

export default function Withdrawals() {
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    merchant: "all",
    provider: "all",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const pageSize = 20;
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["withdrawals"],
    queryFn: async () => {
      const all = await Transaction.list("-created_date", 500);
      return all.filter((t) => t.type === "withdrawal");
    },
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => Merchant.list(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => Provider.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => Transaction.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["withdrawals"]);
      setCreateOpen(false);
    },
  });

  const filteredTransactions = React.useMemo(() => {
    return transactions.filter((tx) => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (
          !tx.transaction_id?.toLowerCase().includes(search) &&
          !tx.merchant_ref?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      if (filters.status !== "all" && tx.status !== filters.status) return false;
      if (filters.merchant !== "all" && tx.merchant_id !== filters.merchant) return false;
      if (filters.provider !== "all" && tx.provider_id !== filters.provider) return false;
      return true;
    });
  }, [transactions, filters]);

  const paginatedTransactions = filteredTransactions.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const stats = React.useMemo(() => {
    const success = transactions.filter((t) => t.status === "success");
    const pending = transactions.filter((t) => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      count: transactions.length,
      successCount: success.length,
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }, [transactions]);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`Withdrawal request submitted successfully! Type: ${data.type}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <ArrowUpFromLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{t("withdrawals")}</h1>
              <p className="text-slate-500">{stats.count} {t("withdrawals")} • ฿{stats.total.toLocaleString()} {t("total")}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-4 text-sm">
            <div className="px-4 py-2 bg-emerald-50 rounded-lg">
              <span className="text-emerald-600 font-medium">{stats.successCount} {t("completed")}</span>
            </div>
            <div className="px-4 py-2 bg-amber-50 rounded-lg">
              <span className="text-amber-600 font-medium">{stats.pendingCount} {t("pending")}</span>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("manualPayout")}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="new" className="space-y-6">
        <TabsList className="bg-white border p-1">
          <TabsTrigger value="new" className="gap-2 px-4">
            <Plus className="h-4 w-4" />
            {t("newWithdrawal")}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2 px-4">
            <History className="h-4 w-4" />
            {t("history")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <WithdrawalForm
            onSubmit={handleWithdrawalSubmit}
            submitting={submitting}
            stats={stats}
            accentColor="blue"
          />
        </TabsContent>

        <TabsContent value="history">
          {/* Filters */}
          <TransactionFilters
            filters={{ ...filters, type: "withdrawal" }}
            onFilterChange={(f) => {
              setFilters(f);
              setPage(0);
            }}
            merchants={merchants}
            providers={providers}
            onReset={() => {
              setFilters({ search: "", status: "all", merchant: "all", provider: "all" });
              setPage(0);
            }}
          />

          {/* Table */}
          <TransactionTable
            transactions={paginatedTransactions}
            isLoading={isLoading}
            onView={setSelectedTransaction}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredTransactions.length)} {t("of")} {filteredTransactions.length}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">{t("page")} {page + 1} {t("of")} {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Sheet */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />

      {/* Create Manual Payout Dialog */}
      <CreatePayoutDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        merchants={merchants}
        providers={providers}
        onCreate={(data) => createMutation.mutate(data)}
      />
    </div>
  );
}

function CreatePayoutDialog({ open, onClose, merchants, providers, onCreate }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    merchant_id: "",
    amount: "",
    bank_code: "",
    bank_account: "",
    customer_name: "",
    customer_id: "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    const txId = "TXN" + Date.now() + Math.random().toString(36).substring(2, 8).toUpperCase();
    const merchant = merchants.find((m) => m.id === formData.merchant_id);
    
    await onCreate({
      transaction_id: txId,
      type: "withdrawal",
      status: "pending",
      merchant_id: formData.merchant_id,
      merchant_code: merchant?.code,
      amount: parseFloat(formData.amount),
      currency: "THB",
      payment_method: "bank_transfer",
      bank_code: formData.bank_code,
      bank_account: formData.bank_account,
      customer_name: formData.customer_name,
      customer_id: formData.customer_id,
      callback_status: "pending",
    });
    
    setSaving(false);
    setFormData({
      merchant_id: "",
      amount: "",
      bank_code: "",
      bank_account: "",
      customer_name: "",
      customer_id: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("manualPayout")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t("merchant")} *</Label>
            <Select
              value={formData.merchant_id}
              onValueChange={(v) => handleChange("merchant_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectMerchant")} />
              </SelectTrigger>
              <SelectContent>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("amount")} (THB) *</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-2">
            <Label>{t("bank")} *</Label>
            <Select
              value={formData.bank_code}
              onValueChange={(v) => handleChange("bank_code", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectBank")} />
              </SelectTrigger>
              <SelectContent>
                {THAI_BANKS.map((bank) => (
                  <SelectItem key={bank.code} value={bank.code}>
                    {bank.code} - {bank.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("bankAccountNumber")} *</Label>
            <Input
              value={formData.bank_account}
              onChange={(e) => handleChange("bank_account", e.target.value)}
              placeholder="e.g., 1234567890"
            />
          </div>

          <div className="space-y-2">
            <Label>{t("accountHolderName")} *</Label>
            <Input
              value={formData.customer_name}
              onChange={(e) => handleChange("customer_name", e.target.value)}
              placeholder={t("enterAccountHolderName")}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("customerId")}</Label>
            <Input
              value={formData.customer_id}
              onChange={(e) => handleChange("customer_id", e.target.value)}
              placeholder={t("optionalCustomerRef")}
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.merchant_id || !formData.amount || !formData.bank_code || !formData.bank_account || !formData.customer_name}
          >
            {saving ? t("creating") : t("createPayout")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}